import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";

import Home from "@/pages/Home";
import Product from "@/pages/Product";
import Features from "@/pages/Features";
import Solutions from "@/pages/Solutions";
import Pricing from "@/pages/Pricing";
import Security from "@/pages/Security";
import Resources from "@/pages/Resources";
import Article from "@/pages/Article";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import IntellectualProperty from "@/pages/IntellectualProperty";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AcceptInvite from "@/pages/AcceptInvite";
import NotFound from "@/pages/NotFound";

import AppLayout from "@/app/AppLayout";
import Onboarding from "@/app/pages/Onboarding";
import CommandCenter from "@/app/pages/CommandCenter";
import MeetingAssist from "@/app/pages/MeetingAssist";
import WorkEngine from "@/app/pages/WorkEngine";
import SalesEngine from "@/app/pages/SalesEngine";
import ProjectsEngine from "@/app/pages/ProjectsEngine";
import CalendarEngine from "@/app/pages/CalendarEngine";
import GoalsPage from "@/app/pages/GoalsPage";
import SettingsLayout from "@/app/pages/SettingsLayout";
import ProfileSettings from "@/app/pages/ProfileSettings";
import OrgSettings from "@/app/pages/OrgSettings";
import MembersSettings from "@/app/pages/MembersSettings";
import PreferencesSettings from "@/app/pages/PreferencesSettings";
import BillingSettings from "@/app/pages/BillingSettings";
import NotificationSettings from "@/app/pages/NotificationSettings";
import HelpFeedback from "@/app/pages/HelpFeedback";
import FoundingAccessPage from "@/app/pages/FoundingAccessPage";
import Board from "@/app/pages/Board";
import BusinessData from "@/app/pages/BusinessData";
import SuperAdminPortal from "@/superadmin/SuperAdminPortal";

function AppRouter() {
  const location = useLocation();

  // Phase 12.4B — Native Google OAuth callback (StUpEco-owned Client) OR
  // legacy Emergent proxy hash callback. AuthCallback handles both.
  if (
    location.pathname === "/auth/google/callback" ||
    location.hash?.includes("session_id=")
  ) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Marketing pages with shared navbar/footer */}
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/product" element={<Product />} />
        <Route path="/features" element={<Features />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/security" element={<Security />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/:slug" element={<Article />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/legal/intellectual-property" element={<IntellectualProperty />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Standalone auth pages */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      {/* Onboarding (protected, standalone) */}
      <Route
        path="/app/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      {/* Protected Workspace Platform (Phase 2 shell) */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CommandCenter />} />
        <Route path="work" element={<WorkEngine />} />
        <Route path="sales" element={<SalesEngine />} />
        <Route path="projects" element={<ProjectsEngine />} />
        <Route path="calendar" element={<CalendarEngine />} />
        <Route path="goals" element={<GoalsPage />} />
        <Route path="meeting" element={<MeetingAssist />} />
        <Route path="board" element={<Board />} />
        <Route path="data" element={<BusinessData />} />
        <Route path="data/:id" element={<BusinessData />} />
        <Route path="founding" element={<FoundingAccessPage />} />
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ProfileSettings />} />
          <Route path="organization" element={<OrgSettings />} />
          <Route path="members" element={<MembersSettings />} />
          <Route path="preferences" element={<PreferencesSettings />} />
          <Route path="billing" element={<BillingSettings />} />
          <Route path="notifications" element={<NotificationSettings />} />
        </Route>
        <Route path="help" element={<HelpFeedback />} />
      </Route>

      {/* Super Admin (server-side authorized; UI hidden from ordinary users) */}
      <Route
        path="/superadmin/*"
        element={
          <ProtectedRoute>
            <SuperAdminPortal />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
